import type { IOgGridTableProps } from '@opengov/components-og-grid-table';
import type { ICellRendererParams, IRowNode } from 'ag-grid-community';
import { ColumnTypes } from '../../../src/components/table/columnTypes';
import { DimensionalSummaryTableRenderer } from '../components/DimensionalSummaryTableRenderer';
import { IDimensionalSortOption } from '../components/DimensionalSortPopper';
import { List } from 'immutable';
import { createSelector } from '@opengov/compose-app/reselect';
import { calendarUnitFormatters } from '@opengov/reporting-formatters/formatters';
import {
  CalendarUnit,
  dateColumnNameToCalendarUnit,
  EReportColumnTypes,
  IDimensionalDateReportColumn,
  IReportColumn,
  IStorageReportColumnRecord
} from '@opengov/reporting-types';

import { reportLocaleText } from '../../common/locale';
import { columnToSortingPossibilities } from '../../common/reportConfiguration/state';
import { IDimensionalAgGridRowItem } from '../../reportExplorer/effects/reportDimensionalEffects';
import { selectAgGridColumnTypes } from '../../reportExplorer/ReportDataTableAgGrid/selectors/selectAgGridColumnTypes';
import {
  selectCoaRowsControls,
  selectDimensionalRowsColumn
} from '../../reportExplorer/selectors/selectReportDimension';
import {
  selectDimensionalReportConfiguration,
  selectDimensionalSummaryColumns
} from '../../reportExplorer/selectors/selectReportExplorerState';
import { dimensionalTableEffects } from '../effects';
import { dimensionalTableActionCreators } from '../state/dimensionTableState';

import { selectDimensionalTableLayoutProps } from './selectDimensionalTableProps';
import { selectDimensionalTreeFilterProps } from './selectDimensionalTreeFilterProps';

let scopedTableLayoutProps;

const summaryGridOptions = (
  autoHeaderName: string,
  isSorted: boolean,
  setActiveDimensionalQueryIndex: (queryIndex: number, nodeId: string) => void,
  getRowClass?: (params: any) => string | string[]
): IOgGridTableProps['gridOptions'] => ({
  autoGroupColumnDef: {
    field: 'name',
    headerName: '',
    cellRendererSelector: undefined,
    cellRendererParams: {
      suppressCount: true,
      onClick: setActiveDimensionalQueryIndex,
      innerRenderer: DimensionalSummaryTableRenderer
    },
    // isSorted,
    pinned: 'left',
    minWidth: 256,
    width: 256 // this is the starting width which should match the css for the controls
  },
  allowContextMenuWithControlKey: false,
  defaultColDef: {
    cellRendererSelector: undefined,
    sortable: false,
    resizable: true,
    tooltipComponentParams: { isDetailTable: false }
  },
  cacheBlockSize: 10000, // determines query row limit
  rowModelType: 'serverSide',
  loadingOverlayComponent: 'CustomLoadingOverlay',
  suppressContextMenu: true,
  treeData: true,
  isServerSideGroup: (dataItem) => dataItem.isGroup && !dataItem.isRowEmpty,
  getServerSideGroupKey: (dataItem) => dataItem.rowId,
  getDataPath: (dataItem) => dataItem.name,
  onRowGroupOpened: (agGridRowEvent) => {
    // agGrid row group event listener to persist expanded state
    // The displayed row index from ag-grid does not correspond to our
    // Account Summary block index (if other blocks are expanded)
    // so we need to ensure we have the right row node
    if (agGridRowEvent?.node) {
      const { expanded: expandedState, id: eventNodeId } = agGridRowEvent.node;
      const groupNodes: IRowNode[] = [];
      agGridRowEvent.api.forEachNode((node) => {
        if (node.data && node.data.isGroup) {
          groupNodes.push(node);
        }
      });
      const expandedRowIndex = groupNodes.findIndex((groupNode) => groupNode.id === eventNodeId);
      if (expandedRowIndex > -1) {
        void dimensionalTableEffects.updateAccountSummaryBlockExpanded({
          accountSummaryBlockIndex: expandedRowIndex,
          expandedState
        });
      }
    }
  },
  getRowClass
});

const getFormatterForColumnHeader = (reportConfig) => {
  const columnsColumn: IDimensionalDateReportColumn | IStorageReportColumnRecord =
    reportConfig.dimensionalSummaryQueries[0] && reportConfig.dimensionalSummaryQueries[0].dimension.columnsColumn;
  let calendarUnit = '';
  let formatter = (domain: string) => domain;
  if (!columnsColumn) {
    return formatter;
  }

  if (columnsColumn.columnType === EReportColumnTypes.dimensional && columnsColumn.dateColumnName) {
    calendarUnit = dateColumnNameToCalendarUnit[columnsColumn.dateColumnName];
  } else if (
    columnsColumn.columnType === EReportColumnTypes.storage &&
    columnsColumn.semanticTypes &&
    (columnsColumn.semanticTypes as List<string>).find((t) => {
      return t.includes && t.includes('fiscal/year');
    })
  ) {
    calendarUnit = CalendarUnit.FiscalYear;
  }

  if (calendarUnit) {
    const { formatter: dateFormatter } = calendarUnitFormatters[calendarUnit];
    formatter = dateFormatter({
      entityFiscalYearStartMonth: reportConfig.presentationConfiguration.entityFiscalYearStartMonth
    });
  }

  return formatter;
};

export const formatCellValue = (params: ICellRendererParams, columnTypes) => {
  const data: IDimensionalAgGridRowItem = params.data;
  if (data && data.name[0] === 'FTE Counts') {
    return columnTypes[ColumnTypes.Number].valueFormatter(params);
  } else {
    return columnTypes[ColumnTypes.Currency].valueFormatter(params);
  }
};

export const selectDimensionalColumnDefs = createSelector(
  selectDimensionalReportConfiguration,
  selectDimensionalSummaryColumns,
  selectAgGridColumnTypes,
  (reportConfiguration, columns: IReportColumn[], columnTypes) => {
    if (!reportConfiguration) return [];
    return columns.map((column) => {
      const headerFormatter = getFormatterForColumnHeader(reportConfiguration);

      return {
        headerName: headerFormatter(column.name),
        field: column.name,
        isValueColumn: true,
        type: ColumnTypes.Currency, // hardcode for now
        headerComponentParams: {
          isFiltered: false, // summary table isn't filtered
          isSorted: false
        },
        valueFormatter: (params) => formatCellValue(params, columnTypes),
        valueGetter: (params) =>
          params.data === undefined || params.data[params.colDef.field] === undefined
            ? '0'
            : params.data[params.colDef.field]
      };
    });
  }
);

const selectDimensionalSummaryTxt = createSelector(reportLocaleText, (txt) => ({
  coaAscending: txt.reportTable.coaAscending,
  coaDescending: txt.reportTable.coaDescending,
  code: txt.reportTable.code,
  codes: txt.reportTable.codes,
  description: txt.description
}));

const selectRowColumnDisplayName = createSelector(
  selectDimensionalRowsColumn,
  selectCoaRowsControls,
  (rowsColumn, rowsControls): string => {
    let selectedRowColum;
    if (rowsControls && rowsControls[0]) {
      rowsControls[0].options.forEach((option) => {
        if (option.children) {
          option.children.forEach((child) => {
            if (child.value === (rowsColumn && rowsColumn.name)) {
              selectedRowColum = child;
            }
          });
        }
      });
    }
    return selectedRowColum && selectedRowColum.label;
  }
);

const setActiveAgGridNodeId = (rowIndex, nodeId) => {
  dimensionalTableActionCreators.changeDrawerVisibility(true);
  dimensionalTableActionCreators.updateControlBlockIndex(nodeId * 1);
};

const getDimensionalSummaryAgGridRowClass = (params) => {
  if (
    // Parent row doesn't get id under params.node.parent
    (params.node.group && Number(params.node.id) === scopedTableLayoutProps.expandedControlBlockIndex) ||
    Number(params.node.parent.id) === scopedTableLayoutProps.expandedControlBlockIndex
  ) {
    return 'ag-row-selected';
  }
  return '';
};

export const selectDimensionalSortOptions = createSelector(
  selectDimensionalReportConfiguration,
  selectDimensionalRowsColumn,
  selectDimensionalTreeFilterProps,
  selectDimensionalSummaryTxt,
  (reportConfiguration, rowsColumn, filterProps, text): IDimensionalSortOption[] => {
    if (!reportConfiguration || !rowsColumn) {
      return [];
    }
    const { descriptionCodeColumnRef, hasCodeColumn, segmentCodeColumnRef, segmentCodeOrder } =
      columnToSortingPossibilities(rowsColumn);

    const options: IDimensionalSortOption[] = [
      {
        ascText: `${text.description} A → Z`,
        descText: `${text.description} Z → A`,
        sortActive: descriptionCodeColumnRef === filterProps.sortColumn,
        sortDirection: filterProps.sortDirection,
        onSortAsc: () => {
          filterProps.onSortAsc(descriptionCodeColumnRef);
        },
        onSortDesc: () => {
          filterProps.onSortDesc(descriptionCodeColumnRef);
        },
        onClearSort: filterProps.onClearSort
      },
      {
        ascText: text.coaAscending,
        descText: text.coaDescending,
        sortActive: segmentCodeOrder === filterProps.sortColumn,
        sortDirection: filterProps.sortDirection,
        onSortAsc: () => {
          filterProps.onSortAsc(segmentCodeOrder);
        },
        onSortDesc: () => {
          filterProps.onSortDesc(segmentCodeOrder);
        },
        onClearSort: filterProps.onClearSort
      }
    ];

    if (hasCodeColumn) {
      options.unshift({
        ascText: `${text.code} 1 → 9`,
        descText: `${text.code} 9 → 1`,
        sortActive: segmentCodeColumnRef === filterProps.sortColumn,
        sortDirection: filterProps.sortDirection,
        onSortAsc: () => {
          filterProps.onSortAsc(segmentCodeColumnRef);
        },
        onSortDesc: () => {
          filterProps.onSortDesc(segmentCodeColumnRef);
        },
        onClearSort: filterProps.onClearSort
      });
    }

    return options;
  }
);

export const selectDimensionalSummaryAgGridProps = createSelector(
  selectDimensionalColumnDefs,
  selectDimensionalReportConfiguration,
  selectAgGridColumnTypes,
  selectDimensionalRowsColumn,
  selectDimensionalTreeFilterProps,
  selectDimensionalSummaryTxt,
  selectRowColumnDisplayName,
  selectDimensionalTableLayoutProps,
  (
    columnDefs,
    reportConfiguration,
    columnTypes,
    rowsColumn,
    filterProps,
    text,
    selectedRowColumnDisplayName,
    dimensionLayoutProps
  ): IOgGridTableProps => {
    /**
     * Passing it as a param to method `summaryGridOptions` doesn't reflect new value
     * Suspecting it's a scope issue but didn't find any other way to resolve it
     */
    scopedTableLayoutProps = dimensionLayoutProps;
    if (!reportConfiguration || !rowsColumn) {
      return {
        columnDefs,
        gridOptions: {
          ...summaryGridOptions('', false, () => {}),
          ...{ columnTypes }
        }
      } as IOgGridTableProps;
    }

    const { descriptionCodeColumnRef, segmentCodeColumnRef, segmentCodeOrder } =
      columnToSortingPossibilities(rowsColumn);
    const isSorted =
      segmentCodeColumnRef === filterProps.sortColumn ||
      descriptionCodeColumnRef === filterProps.sortColumn ||
      segmentCodeOrder === filterProps.sortColumn ||
      rowsColumn.name === filterProps.sortColumn;

    const categorizedByColumnLabel = selectedRowColumnDisplayName;
    const sumGridOptions = summaryGridOptions(
      categorizedByColumnLabel,
      isSorted,
      setActiveAgGridNodeId,
      getDimensionalSummaryAgGridRowClass
    );
    const gridOptions = { ...sumGridOptions, ...{ columnTypes } };

    // onFilterOpened intentionally omitted: this grid has no ag-grid column filters (sortable: false,
    // no filter colDef), so the event never fires.
    return {
      columnDefs,
      gridOptions
    } as IOgGridTableProps;
  }
);
